"use client";

import { startTransition, useEffect, useRef, useState } from "react";
import Image from "next/image";
import {
  BarChart3,
  BellRing,
  FileText,
  LayoutDashboard,
  LineChart,
  MessageSquareText,
  SearchCheck,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { WebsiteCtaLink } from "@/app/(website)/_components/website-cta-link";
import { SampleReportPreview } from "@/app/(website)/_components/sample-report-preview";

type PreviewKind = "report" | "chat" | "competitor" | "image";

type FeatureGalleryItem = {
  id: string;
  name: string;
  eyebrow: string;
  title: string;
  description: string;
  accent: string;
  icon: LucideIcon;
  points: string[];
  metrics: string[];
  preview: PreviewKind;
  imageSrc?: string;
  imageAlt?: string;
  agentImageSrc?: string;
  artifactLayout?: {
    leftCard?: string;
    leftAgent?: string;
    rightCard?: string;
    rightImage?: string;
    rightAgent?: string;
    glow?: string;
  };
};

const featureGalleryItems: FeatureGalleryItem[] = [
  {
    id: "executive-report",
    name: "Executive Report",
    eyebrow: "Every morning, already prepared",
    title: "Daily revenue brief with what changed, what matters, and what to fix first.",
    description:
      "Storita turns store performance, traffic, customer behavior, and competitor movement into a single executive summary you can act on in minutes.",
    accent: "#a855f7",
    icon: FileText,
    points: [
      "Yesterday's revenue, products, and traffic in one brief",
      "Priority actions highlighted automatically",
      "Competitor movement included alongside store performance",
    ],
    metrics: ["Morning brief", "Priority actions", "Traffic + products"],
    preview: "report",
    imageSrc: "/website/images/homepage/daily-report-mail.jpg",
    imageAlt: "Storita daily executive report preview",
    agentImageSrc: "/website/images/agents/sentinal.png",
    artifactLayout: {
      leftCard: "-left-56 top-[18%]",
      leftAgent: "-left-72 bottom-[2%]",
      rightCard: "-right-60 top-[12%]",
      rightImage: "-right-72 bottom-[8%]",
      rightAgent: "-right-[22rem] top-[52%]",
      glow: "-left-64 bottom-[12%]",
    },
  },
  {
    id: "ask-your-store",
    name: "Ask Your Store",
    eyebrow: "Conversation instead of dashboard-hopping",
    title: "Ask anything about your store and get a grounded answer instantly.",
    description:
      "Instead of stitching together reports, ask direct questions, follow up naturally, and keep drilling down until the cause becomes obvious.",
    accent: "#38bdf8",
    icon: MessageSquareText,
    points: [
      "Natural follow-up questions",
      "Answers grounded in real store data",
      "Faster decisions without building custom reports",
    ],
    metrics: ["Live answers", "Follow-ups", "Grounded in data"],
    preview: "chat",
    agentImageSrc: "/website/images/agents/analyst.png",
    artifactLayout: {
      leftCard: "-left-56 top-[30%]",
      leftAgent: "-left-72 bottom-[10%]",
      rightCard: "-right-60 top-[20%]",
      rightAgent: "-right-[22rem] top-[44%]",
      glow: "-left-64 bottom-[24%]",
    },
  },
  {
    id: "competitor-monitoring",
    name: "Competitor Monitoring",
    eyebrow: "See movement before it hurts margin",
    title: "Track pricing shifts, product moves, and changes worth reacting to.",
    description:
      "Storita keeps watch on the market around you, so competitor changes do not sit unnoticed while your store keeps spending and selling against stale assumptions.",
    accent: "#22c55e",
    icon: SearchCheck,
    points: [
      "Pricing changes summarized daily",
      "New competitor product movement surfaced quickly",
      "Highlights where your store may be exposed",
    ],
    metrics: ["Price gaps", "New movers", "Daily watch"],
    preview: "competitor",
    agentImageSrc: "/website/images/agents/architect.png",
    artifactLayout: {
      leftCard: "-left-56 top-[14%]",
      leftAgent: "-left-72 bottom-[6%]",
      rightCard: "-right-60 top-[24%]",
      glow: "-left-64 bottom-[28%]",
    },
  },
  {
    id: "smart-questions",
    name: "Smart Questions",
    eyebrow: "Find what you would not think to ask",
    title: "Storita suggests the next questions your business needs answered.",
    description:
      "The system does not just wait for prompts. It surfaces smart follow-ups that uncover inefficiencies, opportunities, and hidden operational risk.",
    accent: "#f97316",
    icon: Sparkles,
    points: [
      "Prompts based on your real store context",
      "Uncovers overlooked patterns and bottlenecks",
      "Turns curiosity into concrete investigation",
    ],
    metrics: ["Next best question", "Anomaly prompts", "Hidden patterns"],
    preview: "image",
    imageSrc: "/website/images/features/feature_ai_generated_questions.webp",
    imageAlt: "Storita smart questions feature preview",
    agentImageSrc: "/website/images/agents/explorer.png",
    artifactLayout: {
      leftCard: "-left-56 top-[26%]",
      leftAgent: "-left-72 bottom-[14%]",
      rightCard: "-right-60 top-[10%]",
      rightImage: "-right-72 bottom-[16%]",
      rightAgent: "-right-[22rem] top-[40%]",
      glow: "-left-64 bottom-[18%]",
    },
  },
  {
    id: "instant-dashboards",
    name: "Instant Dashboards",
    eyebrow: "Pin what matters, skip the setup",
    title: "Build live dashboards from answers with a single click.",
    description:
      "When a chart matters, Storita turns it into an always-on dashboard view immediately, without the usual analytics setup work.",
    accent: "#eab308",
    icon: LayoutDashboard,
    points: [
      "Create dashboards from AI answers instantly",
      "Keep only the metrics your team actually uses",
      "Real-time views that evolve with the business",
    ],
    metrics: ["1-click charts", "Live dashboards", "Share with team"],
    preview: "image",
    imageSrc: "/website/images/features/feature_instant_dashboards.webp",
    imageAlt: "Storita instant dashboards preview",
    agentImageSrc: "/website/images/agents/manager.png",
    artifactLayout: {
      leftCard: "-left-56 top-[20%]",
      leftAgent: "-left-72 bottom-[0%]",
      rightCard: "-right-60 top-[18%]",
      rightImage: "-right-76 bottom-[4%]",
      rightAgent: "-right-[22rem] top-[56%]",
      glow: "-left-64 bottom-[10%]",
    },
  },
  {
    id: "traffic-intelligence",
    name: "Traffic Intelligence",
    eyebrow: "Know what traffic is really doing",
    title: "Watch store trends, marketing performance, and AI or LLM traffic signals in one place.",
    description:
      "Storita helps separate useful demand from noise, so you can see where traffic is slipping, where spend is leaking, and whether AI tools are sending customers your way.",
    accent: "#ec4899",
    icon: LineChart,
    points: [
      "Traffic shifts highlighted early",
      "LLM and AI traffic visible alongside other channels",
      "Helpful context before you increase spend or panic",
    ],
    metrics: ["Traffic shifts", "LLM signals", "Channel context"],
    preview: "image",
    imageSrc: "/website/images/homepage/graphs.png",
    imageAlt: "Storita traffic and performance graphs",
    agentImageSrc: "/website/images/agents/explorer.png",
    artifactLayout: {
      leftCard: "-left-56 top-[12%]",
      leftAgent: "-left-72 bottom-[18%]",
      rightCard: "-right-60 top-[28%]",
      rightImage: "-right-82 bottom-[10%]",
      rightAgent: "-right-[22rem] top-[46%]",
      glow: "-left-64 bottom-[22%]",
    },
  },
  {
    id: "mobile-alerts",
    name: "Mobile Alerts",
    eyebrow: "Your store, always within reach",
    title: "Get alerts, answers, and fast context even when you are away from your desk.",
    description:
      "When something moves, Storita keeps you close to the signal from your phone so important issues do not wait until the next dashboard review.",
    accent: "#8b5cf6",
    icon: BellRing,
    points: [
      "Critical alerts on the go",
      "Ask follow-up questions from your phone",
      "Keep tabs on revenue risk without logging into five tools",
    ],
    metrics: ["Instant alerts", "Mobile chat", "Always on"],
    preview: "image",
    imageSrc: "/website/images/features/mobile_dashboard.webp",
    imageAlt: "Storita mobile dashboard preview",
    agentImageSrc: "/website/images/agents/manager.png",
    artifactLayout: {
      leftCard: "-left-56 top-[34%]",
      leftAgent: "-left-72 bottom-[8%]",
      rightCard: "-right-60 top-[14%]",
      rightImage: "-right-72 bottom-[20%]",
      rightAgent: "-right-[22rem] top-[58%]",
      glow: "-left-64 bottom-[30%]",
    },
  },
];

function getRelativeOffset(index: number, activeIndex: number, total: number) {
  let offset = index - activeIndex;
  const half = Math.floor(total / 2);

  if (offset > half) {
    offset -= total;
  }

  if (offset < -half) {
    offset += total;
  }

  return offset;
}

function FeaturePreview({ item, isActive }: { item: FeatureGalleryItem; isActive: boolean }) {
  if (item.preview === "chat") {
    return (
      <div className="flex h-full flex-col gap-4 bg-[radial-gradient(circle_at_top_left,rgba(56,189,248,0.16),transparent_45%),linear-gradient(180deg,rgba(2,6,23,0.98),rgba(15,23,42,0.94))] p-5 md:p-6">
        <div className="flex items-center justify-between rounded-2xl border border-slate-800/80 bg-slate-950/70 px-4 py-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-sky-300">Live conversation</p>
            <p className="mt-1 text-sm text-slate-400">Ask. Follow up. Keep going until the cause is clear.</p>
          </div>
          <div className="rounded-full border border-sky-400/30 bg-sky-400/10 px-3 py-1 text-xs font-semibold text-sky-200">
            Response ready
          </div>
        </div>

        <div className="space-y-3">
          <div className="ml-auto max-w-[78%] rounded-[1.35rem] rounded-br-md bg-sky-500 px-4 py-3 text-sm font-medium text-white shadow-lg shadow-sky-950/40">
            Which products are getting traffic but still failing to convert this week?
          </div>

          <div className="max-w-[88%] rounded-[1.35rem] rounded-bl-md border border-slate-800/80 bg-slate-900/90 px-4 py-4 text-sm text-slate-200 shadow-lg shadow-black/30">
            <p className="mb-3 text-slate-100">
              Three products stand out. Linen Travel Bag has strong traffic but weak conversion after a price increase. Ceramic Brew Set is drawing AI referral traffic with low add-to-cart intent.
            </p>
            <div className="grid gap-2 sm:grid-cols-3">
              {[
                ["Linen Travel Bag", "-1.9% CVR"],
                ["Ceramic Brew Set", "AI traffic spike"],
                ["Starter Bundle", "Cart exits rising"],
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-slate-800 bg-slate-950/80 px-3 py-3">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Product</p>
                  <p className="mt-2 text-sm font-semibold text-white">{label}</p>
                  <p className="mt-1 text-xs text-sky-300">{value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-auto grid grid-cols-3 gap-3">
          {[56, 81, 68].map((value, index) => (
            <div key={value} className="rounded-2xl border border-slate-800/80 bg-slate-950/75 p-3">
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Follow-up {index + 1}</p>
              <div className="mt-3 h-2 rounded-full bg-slate-800">
                <div
                  className="h-2 rounded-full bg-sky-400 transition-all duration-700"
                  style={{ width: isActive ? `${value}%` : "35%" }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (item.preview === "competitor") {
    return (
      <div className="flex h-full flex-col gap-4 bg-[radial-gradient(circle_at_top_right,rgba(34,197,94,0.18),transparent_42%),linear-gradient(180deg,rgba(2,6,23,0.98),rgba(15,23,42,0.94))] p-5 md:p-6">
        <div className="rounded-2xl border border-slate-800/80 bg-slate-950/80 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-emerald-300">Market watch</p>
              <p className="mt-1 text-sm text-slate-400">Competitor changes summarized before they drag on your performance.</p>
            </div>
            <div className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-200">
              4 changes today
            </div>
          </div>
        </div>

        <div className="rounded-[1.6rem] border border-slate-800/80 bg-slate-950/75 p-3 md:p-4">
          <div className="grid grid-cols-[1.5fr_1fr_1fr_auto] gap-3 border-b border-slate-800/80 px-2 pb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            <span>Product</span>
            <span>Your price</span>
            <span>Competitor</span>
            <span>Gap</span>
          </div>
          <div className="mt-2 space-y-2">
            {[
              ["Travel Mug", "$39", "$35", "-10%"],
              ["Desk Lamp", "$64", "$64", "match"],
              ["Standing Pad", "$89", "$82", "-8%"],
              ["Starter Kit", "$119", "$128", "+7%"],
            ].map(([label, ours, theirs, delta], index) => {
              const negative = delta.startsWith("-");
              const neutral = delta === "match";

              return (
                <div
                  key={label}
                  className="grid grid-cols-[1.5fr_1fr_1fr_auto] items-center gap-3 rounded-2xl border border-slate-800/70 bg-slate-900/70 px-3 py-3 text-sm text-slate-200"
                  style={{ transform: isActive ? `translateX(${index % 2 === 0 ? 0 : 4}px)` : "translateX(0px)" }}
                >
                  <span className="font-medium text-white">{label}</span>
                  <span>{ours}</span>
                  <span>{theirs}</span>
                  <span
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                      neutral
                        ? "bg-slate-800 text-slate-300"
                        : negative
                          ? "bg-rose-500/15 text-rose-200"
                          : "bg-emerald-500/15 text-emerald-200"
                    }`}
                  >
                    {delta}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[
            ["2", "pricing alerts"],
            ["1", "new product"],
            ["1", "promo ended"],
          ].map(([value, label]) => (
            <div key={label} className="rounded-2xl border border-slate-800/80 bg-slate-950/80 p-3 text-center">
              <p className="text-2xl font-semibold text-white">{value}</p>
              <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-slate-500">{label}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (item.preview === "report") {
    return (
      <div className="h-full p-4 md:p-5">
        <SampleReportPreview compact initialRange="daily" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.08),transparent_32%),linear-gradient(180deg,rgba(2,6,23,0.98),rgba(15,23,42,0.94))] p-5 md:p-6">
      <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-slate-800/80 bg-slate-950/80 px-4 py-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-300">Feature preview</p>
          <p className="mt-1 text-sm text-slate-400">A concrete look at how this part of Storita feels in practice.</p>
        </div>
        <div className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1 text-xs font-semibold text-slate-300">
          Live concept
        </div>
      </div>

      <div className="relative min-h-[320px] flex-1 overflow-hidden rounded-[1.75rem] border border-slate-800/80 bg-slate-950/70 p-3 shadow-2xl shadow-black/20">
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-transparent" />
        {item.imageSrc && item.imageAlt ? (
          <Image
            src={item.imageSrc}
            alt={item.imageAlt}
            fill
            className="object-cover object-top"
            sizes="(max-width: 1024px) 100vw, 60vw"
          />
        ) : null}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        {item.metrics.map((metric) => (
          <div key={metric} className="rounded-2xl border border-slate-800/80 bg-slate-950/80 px-3 py-3 text-center">
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Signal</p>
            <p className="mt-2 text-sm font-semibold text-white">{metric}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function FeatureArtifacts({ item, isActive }: { item: FeatureGalleryItem; isActive: boolean }) {
  const layout = item.artifactLayout ?? {};

  return (
    <>
      <div
        className={`pointer-events-none absolute z-40 hidden w-52 rounded-[1.5rem] border border-white/10 bg-slate-950/80 p-4 shadow-[0_24px_70px_rgba(2,6,23,0.65)] backdrop-blur-xl transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] md:block ${layout.leftCard ?? "-left-56 top-[24%] rotate-[-8deg]"} ${
          isActive ? "translate-x-0 translate-y-0 rotate-[-8deg] opacity-100" : "-translate-x-24 translate-y-4 rotate-[-14deg] opacity-0"
        }`}
        style={{ boxShadow: `0 24px 70px color-mix(in srgb, ${item.accent} 18%, rgba(2,6,23,0.65))` }}
      >
        <div className="flex items-center justify-between gap-3">
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Signal</span>
          <span
            className="rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em]"
            style={{ background: `color-mix(in srgb, ${item.accent} 16%, rgba(15,23,42,0.96))`, color: item.accent }}
          >
            Live
          </span>
        </div>
        <p className="mt-4 text-base font-semibold text-white">{item.metrics[0]}</p>
        <p className="mt-2 text-sm leading-relaxed text-slate-400">{item.points[0]}</p>
      </div>

      {item.agentImageSrc ? (
        <div
          className={`pointer-events-none absolute z-30 hidden h-52 w-52 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] lg:block ${layout.leftAgent ?? "-left-72 bottom-[4%] rotate-[-7deg]"} ${
            isActive ? "translate-x-0 translate-y-0 rotate-[-7deg] opacity-100" : "-translate-x-20 translate-y-10 rotate-[-11deg] opacity-0"
          }`}
        >
          <div className="relative h-full w-full drop-shadow-[0_26px_70px_rgba(2,6,23,0.7)]">
            <Image src={item.agentImageSrc} alt={`${item.name} agent art`} fill className="object-contain" sizes="208px" />
          </div>
        </div>
      ) : null}

      <div
        className={`pointer-events-none absolute z-40 hidden w-56 rounded-[1.55rem] border border-white/10 bg-slate-950/78 p-4 shadow-[0_28px_80px_rgba(2,6,23,0.66)] backdrop-blur-xl transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] lg:block ${layout.rightCard ?? "-right-60 top-[16%] rotate-[7deg]"} ${
          isActive ? "translate-x-0 translate-y-0 rotate-[7deg] opacity-100" : "translate-x-24 -translate-y-2 rotate-[12deg] opacity-0"
        }`}
        style={{ boxShadow: `0 28px 80px color-mix(in srgb, ${item.accent} 14%, rgba(2,6,23,0.66))` }}
      >
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.accent }} />
          <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Why it matters</span>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-slate-200">{item.points[1]}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {item.metrics.slice(1, 3).map((metric) => (
            <span
              key={metric}
              className="rounded-full border px-2.5 py-1 text-[11px] font-medium text-slate-200"
              style={{
                borderColor: `color-mix(in srgb, ${item.accent} 34%, rgba(71,85,105,0.7))`,
                background: `color-mix(in srgb, ${item.accent} 10%, rgba(15,23,42,0.94))`,
              }}
            >
              {metric}
            </span>
          ))}
        </div>
      </div>

      {item.imageSrc ? (
        <div
          className={`pointer-events-none absolute z-30 hidden h-40 w-40 overflow-hidden rounded-[1.8rem] border border-white/10 bg-slate-950/80 shadow-[0_30px_80px_rgba(2,6,23,0.68)] backdrop-blur-xl transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] xl:block ${layout.rightImage ?? "-right-72 bottom-[10%] rotate-[10deg]"} ${
            isActive ? "translate-x-0 translate-y-0 rotate-[10deg] opacity-100" : "translate-x-28 translate-y-8 rotate-[16deg] opacity-0"
          }`}
          style={{ boxShadow: `0 30px 80px color-mix(in srgb, ${item.accent} 12%, rgba(2,6,23,0.68))` }}
        >
          <Image src={item.imageSrc} alt={item.imageAlt ?? item.name} fill className="object-cover object-top" sizes="160px" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
        </div>
      ) : null}

      {item.agentImageSrc ? (
        <div
          className={`pointer-events-none absolute z-20 hidden h-44 w-44 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] xl:block ${layout.rightAgent ?? "-right-[22rem] top-[48%] rotate-[11deg]"} ${
            isActive ? "translate-x-0 translate-y-0 rotate-[11deg] opacity-100" : "translate-x-24 -translate-y-6 rotate-[16deg] opacity-0"
          }`}
        >
          <div className="relative h-full w-full opacity-90 drop-shadow-[0_24px_64px_rgba(2,6,23,0.72)]">
            <Image src={item.agentImageSrc} alt={`${item.name} agent art`} fill className="object-contain" sizes="176px" />
          </div>
        </div>
      ) : null}

      <div
        className={`pointer-events-none absolute z-20 hidden h-28 w-28 rounded-full blur-2xl transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] lg:block ${layout.glow ?? "-left-64 bottom-[18%]"} ${
          isActive ? "translate-x-0 opacity-100" : "-translate-x-16 opacity-0"
        }`}
        style={{ background: `color-mix(in srgb, ${item.accent} 22%, transparent)` }}
      />
    </>
  );
}

export function FeatureGallery() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);

  const scrollGalleryIntoView = () => {
    if (!sectionRef.current) {
      return;
    }

    const rect = sectionRef.current.getBoundingClientRect();
    const topOffset = 88;
    const bottomOffset = 32;
    const isFullyVisible = rect.top >= topOffset && rect.bottom <= window.innerHeight - bottomOffset;

    if (isFullyVisible) {
      return;
    }

    const targetTop = window.scrollY + rect.top - topOffset;
    window.scrollTo({ top: Math.max(targetTop, 0), behavior: "smooth" });
  };

  useEffect(() => {
    if (!hasInteracted) {
      return;
    }

    const timeout = window.setTimeout(() => {
      startTransition(() => {
        setActiveIndex((current) => (current + 1) % featureGalleryItems.length);
      });
    }, 5200);

    return () => window.clearTimeout(timeout);
  }, [activeIndex, hasInteracted]);

  return (
    <section ref={sectionRef} className="overflow-hidden bg-slate-950 pt-8 pb-24 md:pt-12 md:pb-24">
      <div className="container mx-auto px-4 md:px-6">
        <ScrollReveal>
          <div className="relative min-h-[640px] overflow-visible rounded-[2.4rem] border border-slate-800/70 bg-slate-900/45 p-3 md:p-5 shadow-[0_40px_120px_rgba(2,6,23,0.55)]">
            <div className="absolute left-3 top-1/2 z-50 flex -translate-y-1/2 flex-col gap-2 rounded-[1.6rem] border border-white/10 bg-slate-950/78 p-2 shadow-[0_20px_50px_rgba(2,6,23,0.72)] backdrop-blur-xl md:hidden">
              {featureGalleryItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = index === activeIndex;

                return (
                  <button
                    key={`${item.id}-mobile`}
                    type="button"
                    aria-label={item.name}
                    aria-pressed={isActive}
                    onClick={() => {
                      scrollGalleryIntoView();
                      startTransition(() => {
                        setHasInteracted(true);
                        setActiveIndex(index);
                      });
                    }}
                    className={`group relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl border transition-all duration-500 ${
                      isActive ? "border-transparent bg-slate-900" : "border-slate-800/90 bg-slate-950/70"
                    }`}
                    style={
                      isActive
                        ? {
                            boxShadow: `0 12px 30px color-mix(in srgb, ${item.accent} 20%, transparent)`,
                          }
                        : undefined
                    }
                  >
                    <div
                      className={`absolute inset-0 opacity-0 transition-opacity duration-500 ${isActive ? "opacity-100" : "group-active:opacity-100"}`}
                      style={{
                        background: `linear-gradient(135deg, color-mix(in srgb, ${item.accent} 18%, transparent), transparent 70%)`,
                      }}
                    />
                    <div
                      className="relative flex h-9 w-9 items-center justify-center rounded-xl border"
                      style={{
                        borderColor: `color-mix(in srgb, ${item.accent} 40%, rgba(51,65,85,0.9))`,
                        background: `color-mix(in srgb, ${item.accent} 12%, rgba(2,6,23,0.95))`,
                        color: item.accent,
                      }}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                  </button>
                );
              })}
            </div>

            {!hasInteracted ? (
              <div className="feature-gallery-doodle-note-mobile absolute left-[4.6rem] top-6 z-50 flex max-w-[11rem] items-start gap-2 text-white/95 md:hidden">
                <svg
                  width="44"
                  height="54"
                  viewBox="0 0 44 54"
                  fill="none"
                  aria-hidden="true"
                  className="feature-gallery-doodle-arrow-mobile"
                >
                  <path
                    d="M37 7C30 11 24 15 19 22C14 28 11 34 10 43"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path d="M17 37C14 39 12 41 10 43" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                  <path d="M7 36C8 39 9 41 10 43" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
                <span className="feature-gallery-doodle-text text-[13px] leading-tight">
                  Click here
                  <br />
                  Select a feature to explore
                </span>
              </div>
            ) : null}

            <div className="absolute left-3 top-3 z-50 hidden w-[min(560px,calc(100%-1.5rem))] -translate-x-[6%] rounded-[2rem] border border-white/10 bg-slate-950/78 p-4 shadow-[0_25px_70px_rgba(2,6,23,0.7)] backdrop-blur-xl md:left-6 md:top-8 md:block md:-translate-x-[12%] md:p-5">
              <p
                className="mb-4 text-sm font-semibold uppercase tracking-[0.28em] text-slate-300"
                style={{
                  backgroundImage: "linear-gradient(90deg,#c084fc,#67e8f9,#86efac,#fde68a,#f9a8d4,#c084fc)",
                  backgroundSize: "200% 100%",
                  color: "transparent",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  animation: "feature-gallery-swoosh 7s cubic-bezier(0.4, 0, 0.2, 1) infinite",
                }}
              >
                What would you like to explore?
              </p>

              <div role="tablist" aria-label="Storita feature gallery" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {featureGalleryItems.map((item, index) => {
                  const Icon = item.icon;
                  const isActive = index === activeIndex;

                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      aria-controls={`feature-panel-${item.id}`}
                      onClick={() => {
                        scrollGalleryIntoView();
                        startTransition(() => {
                          setHasInteracted(true);
                          setActiveIndex(index);
                        });
                      }}
                      className={`group relative overflow-hidden rounded-[1.35rem] border px-4 py-4 text-left transition-all duration-500 ${
                        isActive
                          ? "border-transparent bg-slate-900"
                          : "border-slate-800/90 bg-slate-950/65 hover:border-slate-700 hover:bg-slate-900/80"
                      }`}
                      style={
                        isActive
                          ? {
                              boxShadow: `0 14px 34px color-mix(in srgb, ${item.accent} 20%, transparent)`,
                            }
                          : undefined
                      }
                    >
                      <div
                        className={`absolute inset-0 opacity-0 transition-opacity duration-500 ${isActive ? "opacity-100" : "group-hover:opacity-100"}`}
                        style={{
                          background: `linear-gradient(135deg, color-mix(in srgb, ${item.accent} 18%, transparent), transparent 70%)`,
                        }}
                      />
                      <div className="relative flex items-start gap-3.5">
                        <div
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border"
                          style={{
                            borderColor: `color-mix(in srgb, ${item.accent} 40%, rgba(51,65,85,0.9))`,
                            background: `color-mix(in srgb, ${item.accent} 12%, rgba(2,6,23,0.95))`,
                            color: item.accent,
                          }}
                        >
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[15px] font-semibold leading-snug text-white">{item.name}</p>
                          <p className="mt-1 text-xs leading-relaxed text-slate-400">{item.eyebrow}</p>
                        </div>
                        <span
                          className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full border px-1.5 text-[10px] font-bold transition-all ${
                            isActive ? "opacity-100" : "opacity-55"
                          }`}
                          style={{
                            borderColor: `color-mix(in srgb, ${item.accent} 50%, rgba(100,116,139,0.5))`,
                            color: item.accent,
                            background: `color-mix(in srgb, ${item.accent} 12%, rgba(15,23,42,0.98))`,
                          }}
                        >
                          {index + 1}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-4 flex items-center justify-between gap-3 rounded-[1.35rem] border border-white/10 bg-slate-900/70 px-4 py-3.5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Ready to explore deeper?</p>
                  <p className="mt-1 text-sm text-slate-300">Open Storita and start with your own store.</p>
                </div>
                <WebsiteCtaLink href="/login">Start Now</WebsiteCtaLink>
              </div>

              {!hasInteracted ? (
                <div className="feature-gallery-doodle-note absolute -right-[6.6rem] top-16 flex flex-col items-center text-white/95">
                  <svg
                    width="78"
                    height="42"
                    viewBox="0 0 78 42"
                    fill="none"
                    aria-hidden="true"
                    className="feature-gallery-doodle-arrow"
                  >
                    <path
                      d="M69 8C60 10 52 13 45 18C37 24 30 27 19 28"
                      stroke="currentColor"
                      strokeWidth="3.1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M26 18C18 22 14 25 9 30"
                      stroke="currentColor"
                      strokeWidth="3.1"
                      strokeLinecap="round"
                    />
                    <path
                      d="M25 36C18 32 14 31 9 30"
                      stroke="currentColor"
                      strokeWidth="3.1"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span className="feature-gallery-doodle-text mt-[-0.15rem] text-base leading-none">
                    Choose here
                  </span>
                </div>
              ) : null}
            </div>

            <div className="absolute inset-0 rounded-[2.4rem] bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.06),transparent_40%),radial-gradient(circle_at_bottom_left,rgba(168,85,247,0.18),transparent_34%)]" />

            {!hasInteracted ? (
              <div className="pointer-events-none absolute inset-0 z-30 rounded-[2.4rem] bg-[radial-gradient(circle_at_20%_25%,rgba(56,189,248,0.18),transparent_22%),radial-gradient(circle_at_78%_22%,rgba(168,85,247,0.2),transparent_24%),radial-gradient(circle_at_72%_76%,rgba(236,72,153,0.18),transparent_20%),linear-gradient(135deg,rgba(2,6,23,0.35),rgba(15,23,42,0.12))] backdrop-blur-[18px]" />
            ) : null}

            {featureGalleryItems.map((item, index) => {
              const relativeOffset = getRelativeOffset(index, activeIndex, featureGalleryItems.length);
              const distance = Math.abs(relativeOffset);
              const isActive = relativeOffset === 0;
              const isFocused = hasInteracted && isActive;
              const showArtifacts = isActive;

              const xOffset = relativeOffset * 44;
              const yOffset = distance * 16;
              const rotation = relativeOffset * 2.5;
              const scale = hasInteracted
                ? isActive
                  ? 1
                  : Math.max(0.86, 0.96 - distance * 0.05)
                : 0.96 - distance * 0.03;
              const opacity = hasInteracted
                ? isActive
                  ? 1
                  : distance === 1
                    ? 0.34
                    : distance === 2
                      ? 0.14
                      : 0.04
                : index === 0
                  ? 0.88
                  : distance === 1
                    ? 0.36
                    : 0.16;
              const blur = hasInteracted
                ? isActive
                  ? 0
                  : distance === 1
                    ? 8
                    : distance === 2
                      ? 14
                      : 18
                : index === 0
                  ? 18
                  : distance === 1
                    ? 22
                    : 26;

              return (
                <div
                  key={item.id}
                  id={`feature-panel-${item.id}`}
                  role="tabpanel"
                  aria-hidden={!isFocused}
                  className="absolute inset-3 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] md:inset-5"
                  style={{
                    zIndex: 30 - distance,
                    opacity,
                    filter: `blur(${blur}px) saturate(${hasInteracted ? (isActive ? 1 : 0.7) : 1.15})`,
                    transform: `translate3d(${xOffset}px, ${yOffset}px, 0) scale(${scale}) rotate(${rotation}deg)`,
                    pointerEvents: isFocused ? "auto" : "none",
                  }}
                >
                  <div
                    className="h-full overflow-hidden rounded-[2rem] border border-slate-800/80 bg-slate-950/95 shadow-[0_30px_80px_rgba(2,6,23,0.55)]"
                    style={{
                      boxShadow: isFocused
                        ? `0 30px 90px color-mix(in srgb, ${item.accent} 16%, rgba(2,6,23,0.72))`
                        : undefined,
                    }}
                  >
                    <FeaturePreview item={item} isActive={isFocused} />
                  </div>

                  <FeatureArtifacts item={item} isActive={showArtifacts} />
                </div>
              );
            })}

            <div className="pointer-events-none absolute inset-x-3 bottom-3 z-40 h-24 rounded-b-[2.1rem] bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent md:inset-x-5 md:bottom-5 md:h-28" />
          </div>
        </ScrollReveal>
      </div>

      <style jsx>{`
        @keyframes feature-gallery-swoosh {
          0% {
            background-position: 200% 50%;
          }

          100% {
            background-position: -200% 50%;
          }
        }

        @keyframes feature-gallery-doodle-float {
          0% {
            transform: translate3d(0, 0, 0) rotate(-6deg);
          }

          100% {
            transform: translate3d(-4px, 6px, 0) rotate(-4deg);
          }
        }

        @keyframes feature-gallery-arrow-wobble {
          0% {
            transform: rotate(0deg) scale(1);
          }

          100% {
            transform: rotate(-2deg) scale(1.02);
          }
        }

        .feature-gallery-doodle-text {
          font-family: "Segoe Print", "Bradley Hand", "Comic Sans MS", cursive;
          text-shadow: 0 10px 24px rgba(2, 6, 23, 0.42);
          animation: feature-gallery-doodle-float 3.2s ease-in-out infinite alternate;
        }

        .feature-gallery-doodle-note {
          filter: drop-shadow(0 10px 18px rgba(2, 6, 23, 0.36));
        }

        .feature-gallery-doodle-note-mobile {
          filter: drop-shadow(0 10px 18px rgba(2, 6, 23, 0.36));
        }

        .feature-gallery-doodle-arrow {
          filter: drop-shadow(0 10px 20px rgba(2, 6, 23, 0.34));
          animation: feature-gallery-arrow-wobble 2.4s ease-in-out infinite alternate;
          transform-origin: 88% 34%;
        }

        .feature-gallery-doodle-arrow-mobile {
          flex-shrink: 0;
          filter: drop-shadow(0 8px 18px rgba(2, 6, 23, 0.32));
          animation: feature-gallery-arrow-wobble 2.4s ease-in-out infinite alternate;
          transform-origin: 32% 76%;
        }
      `}</style>
    </section>
  );
}